/**
 * Orders are the operational record of anything a customer actually committed
 * to — a purchase, booking, rental or paid service. Conversations stay in
 * Messages; interest stays in Leads.
 */
export type OrderStatus = 'new' | 'progress' | 'fulfilled' | 'cancelled'

/** Payment is deliberately separate from fulfilment. */
export type PaymentStatus = 'paid' | 'pending' | 'failed' | 'refunded'

/**
 * How the customer chose to pay. Cash orders exist without any gateway, so a
 * small merchant can take orders on day one — and an order can be fulfilled
 * while its cash is still outstanding.
 */
export type PaymentMethod = 'online' | 'cash'

export type OrderSource = 'Talab Shop' | 'Website Chat' | 'Talab Discover'

export interface OrderLine {
  name: string
  variant?: string
  qty: number
  price: number
  image: string
}

export interface OrderEvent {
  label: string
  time: string
  by?: string
}

export interface OrderCustomer {
  name: string
  phone: string
  whatsapp?: string
  email?: string
  conversation?: boolean
}

export interface Order {
  id: string
  customer: OrderCustomer
  lines: OrderLine[]
  total: number
  payment: PaymentStatus
  /** Defaults to an online payment when not set. */
  method?: PaymentMethod
  /** Recorded when a merchant collects cash by hand. */
  paidBy?: string
  paidAt?: string
  status: OrderStatus
  /** Full timestamp for the detail header. */
  placed: string
  /** Relative label used in the list. */
  when: string
  /** Sort key — minutes since the order was placed. */
  age: number
  source: OrderSource
  /** Where the work actually happens. */
  managedBy: 'talab' | 'connected'
  externalId?: string
  lastSync?: string
  /** Only the fulfilment facts that apply to this kind of business. */
  details: {
    label: string
    value: string
  }[]
  instructions?: string
  timeline: OrderEvent[]
  fulfilledBy?: string
  fulfilledAt?: string
  cancelReason?: string
}

export const STATUS_LABEL: Record<OrderStatus, string> = {
  new: 'New',
  progress: 'In Progress',
  fulfilled: 'Fulfilled',
  cancelled: 'Cancelled',
}

/** What the customer sees — plain language, never internal wording. */
export const CUSTOMER_STATUS: Record<OrderStatus, string> = {
  new: 'Order received',
  progress: 'In progress',
  fulfilled: 'Completed',
  cancelled: 'Cancelled',
}

export const PAYMENT_LABEL: Record<PaymentStatus, string> = {
  paid: 'Paid',
  pending: 'Pending',
  failed: 'Failed',
  refunded: 'Refunded',
}

/** One universal wording, because not everything on Talab is delivered. */
export const PAYMENT_METHOD_LABEL: Record<PaymentMethod, string> = {
  online: 'Paid online',
  cash: 'Cash / Pay on Delivery',
}

export function methodOf(order: Order): PaymentMethod {
  return order.method ?? 'online'
}

/** A cash order that was completed but never reconciled needs a nudge. */
export function paymentOutstanding(order: Order): boolean {
  return (
    methodOf(order) === 'cash' &&
    order.payment !== 'paid' &&
    order.payment !== 'refunded' &&
    order.status === 'fulfilled'
  )
}

export const CANCEL_REASONS = [
  'Customer requested',
  'Unavailable',
  'Payment issue',
  'Duplicate',
  'Other',
]

export function money(value: number): string {
  return `AED ${value.toLocaleString('en-US')}`
}

const PRADO =
  'https://images.unsplash.com/photo-1550133730-695473e544be?auto=format&fit=crop&q=80&w=400'
const PATROL =
  'https://images.unsplash.com/photo-1533473359331-0135ef1b58bf?auto=format&fit=crop&q=80&w=400'
const CAMRY =
  'https://images.unsplash.com/photo-1621007947382-bb3c3994e3fb?auto=format&fit=crop&q=80&w=400'
const RANGE =
  'https://images.unsplash.com/photo-1606664515524-ed2f786a0bd6?auto=format&fit=crop&q=80&w=400'
const HIACE =
  'https://images.unsplash.com/photo-1559416523-140ddc3d238c?auto=format&fit=crop&q=80&w=400'
const BMW =
  'https://images.unsplash.com/photo-1555215695-3004980ad54e?auto=format&fit=crop&q=80&w=400'
const G63 =
  'https://images.unsplash.com/photo-1520031441872-265e4ff70366?auto=format&fit=crop&q=80&w=400'

/* Grocery and food, so the same Orders page reads correctly for a supermarket,
   a restaurant and a bakery — not just a rental fleet. */
const PRODUCE =
  'https://images.unsplash.com/photo-1610832958506-aa56368176cf?auto=format&fit=crop&q=80&w=400'
const DAIRY =
  'https://images.unsplash.com/photo-1563636619-e9143da7973b?auto=format&fit=crop&q=80&w=400'
const PANTRY =
  'https://images.unsplash.com/photo-1584473457406-6240486418e9?auto=format&fit=crop&q=80&w=400'
const MEZZE =
  'https://images.unsplash.com/photo-1540914124281-342587941389?auto=format&fit=crop&q=80&w=400'
const BIRYANI =
  'https://images.unsplash.com/photo-1563379091339-03b21ab4a4f8?auto=format&fit=crop&q=80&w=400'
const BURGER =
  'https://images.unsplash.com/photo-1568901346375-23c9450c58cd?auto=format&fit=crop&q=80&w=400'
const CAKE =
  'https://images.unsplash.com/photo-1578985545062-69928b1d9587?auto=format&fit=crop&q=80&w=400'
const COFFEE =
  'https://images.unsplash.com/photo-1509042239860-f550ce710b93?auto=format&fit=crop&q=80&w=400'

/**
 * The same records seen from the other side of the counter — what a signed-in
 * shopper has ordered from THIS merchant. Kept separate from `seedOrders` so a
 * storefront can start genuinely empty (which is the honest default for a new
 * customer) and be populated on demand for demonstration.
 */
export const shopperOrders: Order[] = [
  {
    id: 'TLB-1102',
    customer: {
      name: 'Ahmed Hassan',
      phone: '+971 50 441 9022',
      conversation: true,
    },
    lines: [
      {
        name: 'Toyota Prado 2026',
        variant: 'Monthly rental · white',
        qty: 1,
        price: 4500,
        image: PRADO,
      },
    ],
    total: 4500,
    payment: 'pending',
    method: 'cash',
    status: 'new',
    placed: '13 Sep 2026 · 11:42 AM',
    when: 'Today, 11:42 AM',
    age: 34,
    source: 'Talab Shop',
    managedBy: 'talab',
    details: [
      {
        label: 'Rental period',
        value: '15 Sep – 15 Oct 2026',
      },
      {
        label: 'Delivery address',
        value: 'Marina Gate 1, Dubai Marina',
      },
      {
        label: 'Delivery time',
        value: '15 Sep, 9:00 – 11:00 AM',
      },
    ],
    instructions: 'Please call when you reach the tower entrance.',
    timeline: [
      {
        label: 'Order placed',
        time: '11:42 AM',
      },
    ],
  },
  {
    id: 'TLB-1094',
    customer: {
      name: 'Ahmed Hassan',
      phone: '+971 50 441 9022',
      conversation: true,
    },
    lines: [
      {
        name: 'Nissan Patrol 2025',
        variant: 'Weekend rental · 3 days',
        qty: 1,
        price: 1350,
        image: PATROL,
      },
    ],
    total: 1350,
    payment: 'paid',
    status: 'progress',
    placed: '11 Sep 2026 · 4:18 PM',
    when: '2 days ago',
    age: 2900,
    source: 'Talab Shop',
    managedBy: 'talab',
    details: [
      {
        label: 'Rental period',
        value: '19 Sep – 22 Sep 2026',
      },
      {
        label: 'Pickup location',
        value: 'EasyLease Al Quoz branch',
      },
      {
        label: 'Extras',
        value: 'Child seat · Full insurance',
      },
    ],
    timeline: [
      {
        label: 'Order placed',
        time: '11 Sep, 4:18 PM',
      },
      {
        label: 'Payment received',
        time: '11 Sep, 4:19 PM',
      },
      {
        label: 'Order started',
        time: '12 Sep, 9:05 AM',
      },
    ],
  },
  {
    id: 'TLB-1071',
    customer: {
      name: 'Ahmed Hassan',
      phone: '+971 50 441 9022',
    },
    lines: [
      {
        name: 'Toyota Camry 2025',
        variant: 'Daily rental · 2 days',
        qty: 1,
        price: 380,
        image: CAMRY,
      },
    ],
    total: 380,
    payment: 'paid',
    status: 'fulfilled',
    placed: '28 Aug 2026 · 8:30 AM',
    when: '16 days ago',
    age: 23000,
    source: 'Talab Shop',
    managedBy: 'talab',
    details: [
      {
        label: 'Rental period',
        value: '29 Aug – 31 Aug 2026',
      },
      {
        label: 'Returned to',
        value: 'EasyLease Al Quoz branch',
      },
    ],
    timeline: [
      {
        label: 'Order placed',
        time: '28 Aug, 8:30 AM',
      },
      {
        label: 'Payment received',
        time: '28 Aug, 8:31 AM',
      },
      {
        label: 'Completed',
        time: '31 Aug, 6:40 PM',
      },
    ],
    fulfilledAt: '31 Aug, 6:40 PM',
  },
  {
    id: 'TLB-1043',
    customer: {
      name: 'Ahmed Hassan',
      phone: '+971 50 441 9022',
    },
    lines: [
      {
        name: 'BMW 530i 2024',
        variant: 'Monthly rental',
        qty: 1,
        price: 3900,
        image: BMW,
      },
    ],
    total: 3900,
    payment: 'refunded',
    status: 'cancelled',
    placed: '12 Aug 2026 · 2:10 PM',
    when: '1 Aug 2026',
    age: 46000,
    source: 'Talab Shop',
    managedBy: 'talab',
    details: [],
    timeline: [
      {
        label: 'Order placed',
        time: '12 Aug, 2:10 PM',
      },
      {
        label: 'Cancelled by you',
        time: '12 Aug, 6:55 PM',
      },
      {
        label: 'Refund issued',
        time: '13 Aug, 10:02 AM',
      },
    ],
    cancelReason: 'Changed my mind',
  },
]

export const seedOrders: Order[] = [
  {
    id: 'TLB-1055',
    customer: {
      name: 'Huda Mansour',
      phone: '+971 50 993 1187',
      whatsapp: '+971 50 993 1187',
      email: 'huda.m@gmail.com',
      conversation: true,
    },
    lines: [
      {
        name: 'Weekly fresh box',
        variant: 'Large · seasonal produce',
        qty: 1,
        price: 145,
        image: PRODUCE,
      },
      {
        name: 'Full-fat laban 1L',
        variant: 'Al Rawabi',
        qty: 4,
        price: 9,
        image: DAIRY,
      },
      {
        name: 'Free-range eggs',
        variant: 'Tray of 30',
        qty: 1,
        price: 28,
        image: DAIRY,
      },
      {
        name: 'Arabic flatbread',
        variant: 'Pack of 10',
        qty: 2,
        price: 6,
        image: PANTRY,
      },
    ],
    total: 221,
    payment: 'paid',
    status: 'new',
    placed: '13 Sep 2026 · 12:04 PM',
    when: 'Today, 12:04 PM',
    age: 12,
    source: 'Talab Shop',
    managedBy: 'talab',
    details: [
      {
        label: 'Delivery slot',
        value: 'Today, 6:00 – 8:00 PM',
      },
      {
        label: 'Delivery address',
        value: 'Villa 22, Al Safa 1, Dubai',
      },
      {
        label: 'Items',
        value: '8 units · 1 chilled bag',
      },
    ],
    instructions: 'Leave at the gate if nobody answers. No plastic bags please.',
    timeline: [
      {
        label: 'Order placed',
        time: '12:04 PM',
      },
      {
        label: 'Payment received',
        time: '12:04 PM',
      },
    ],
  },
  {
    id: 'TLB-1054',
    customer: {
      name: 'Marina Heights Café',
      phone: '+971 4 445 1180',
      email: 'orders@marinaheights.ae',
    },
    lines: [
      {
        name: 'Espresso beans 1kg',
        variant: 'Dark roast',
        qty: 12,
        price: 78,
        image: COFFEE,
      },
      {
        name: 'Oat milk 1L',
        variant: 'Barista edition · case of 12',
        qty: 4,
        price: 96,
        image: DAIRY,
      },
    ],
    total: 1320,
    payment: 'pending',
    method: 'cash',
    status: 'new',
    placed: '13 Sep 2026 · 11:10 AM',
    when: 'Today, 11:10 AM',
    age: 66,
    source: 'Website Chat',
    managedBy: 'talab',
    details: [
      {
        label: 'Delivery slot',
        value: 'Tomorrow, 7:00 – 9:00 AM',
      },
      {
        label: 'Delivery address',
        value: 'Marina Walk, Tower 4, Dubai',
      },
      {
        label: 'Payment terms',
        value: 'Invoice on delivery',
      },
    ],
    instructions: 'Deliver to the service entrance before opening.',
    timeline: [
      {
        label: 'Order placed',
        time: '11:10 AM',
      },
      {
        label: 'Awaiting payment',
        time: '11:11 AM',
      },
    ],
  },
  {
    id: 'TLB-1053',
    customer: {
      name: 'Tariq Jamal',
      phone: '+971 55 771 4402',
      whatsapp: '+971 55 771 4402',
      conversation: true,
    },
    lines: [
      {
        name: 'Mixed mezze platter',
        variant: 'Serves 8',
        qty: 2,
        price: 180,
        image: MEZZE,
      },
      {
        name: 'Chicken biryani tray',
        variant: 'Family size',
        qty: 1,
        price: 145,
        image: BIRYANI,
      },
    ],
    total: 505,
    payment: 'paid',
    status: 'progress',
    placed: '13 Sep 2026 · 10:48 AM',
    when: 'Today, 10:48 AM',
    age: 88,
    source: 'Talab Shop',
    managedBy: 'talab',
    details: [
      {
        label: 'Ready by',
        value: 'Today, 7:30 PM',
      },
      {
        label: 'Fulfilment',
        value: 'Catering delivery',
      },
      {
        label: 'Delivery address',
        value: 'Al Nahda 2, Sharjah',
      },
      {
        label: 'Guests',
        value: '18 people',
      },
    ],
    instructions: 'One platter without garlic — guest allergy.',
    timeline: [
      {
        label: 'Order placed',
        time: '10:48 AM',
      },
      {
        label: 'Payment received',
        time: '10:49 AM',
      },
      {
        label: 'Order started',
        time: '11:05 AM',
        by: 'Kitchen',
      },
    ],
  },
  {
    id: 'TLB-1052',
    customer: {
      name: 'Jenny Rivera',
      phone: '+971 52 660 9913',
      email: 'jenny.rivera@mail.com',
    },
    lines: [
      {
        name: 'Double smash burger',
        variant: 'No pickles',
        qty: 2,
        price: 42,
        image: BURGER,
      },
      {
        name: 'Truffle fries',
        variant: 'Large',
        qty: 1,
        price: 26,
        image: BURGER,
      },
    ],
    total: 110,
    payment: 'paid',
    status: 'progress',
    placed: '13 Sep 2026 · 12:22 PM',
    when: 'Today, 12:22 PM',
    age: 6,
    source: 'Website Chat',
    managedBy: 'connected',
    externalId: 'POS-44190',
    lastSync: '1 min ago',
    details: [
      {
        label: 'Fulfilment',
        value: 'Rider delivery',
      },
      {
        label: 'Ready by',
        value: 'Today, 12:45 PM',
      },
      {
        label: 'Delivery address',
        value: 'JLT Cluster D, Tower 2, Apt 1104',
      },
    ],
    timeline: [
      {
        label: 'Order placed',
        time: '12:22 PM',
      },
      {
        label: 'Payment received',
        time: '12:22 PM',
      },
      {
        label: 'Sent to kitchen system',
        time: '12:22 PM',
      },
      {
        label: 'Preparing',
        time: '12:26 PM',
        by: 'Connected system',
      },
    ],
  },
  {
    id: 'TLB-1051',
    customer: {
      name: 'Noor Al Zaabi',
      phone: '+971 56 118 2204',
      whatsapp: '+971 56 118 2204',
    },
    lines: [
      {
        name: 'Pistachio celebration cake',
        variant: '2kg · "Happy 6th Zayd"',
        qty: 1,
        price: 320,
        image: CAKE,
      },
    ],
    total: 320,
    payment: 'paid',
    status: 'fulfilled',
    placed: '12 Sep 2026 · 09:30 AM',
    when: 'Yesterday, 9:30 AM',
    age: 1600,
    source: 'Talab Discover',
    managedBy: 'talab',
    fulfilledBy: 'Mariam S.',
    fulfilledAt: '12 Sep 2026 · 4:10 PM',
    details: [
      {
        label: 'Fulfilment',
        value: 'Store pickup',
      },
      {
        label: 'Pickup location',
        value: 'Jumeirah branch',
      },
      {
        label: 'Collected',
        value: '12 Sep, 4:10 PM',
      },
    ],
    instructions: 'Candles and a cake knife in the box.',
    timeline: [
      {
        label: 'Order placed',
        time: '9:30 AM',
      },
      {
        label: 'Payment received',
        time: '9:31 AM',
      },
      {
        label: 'Order started',
        time: '10:15 AM',
        by: 'Mariam S.',
      },
      {
        label: 'Fulfilled',
        time: '4:10 PM',
        by: 'Mariam S.',
      },
    ],
  },
  {
    id: 'TLB-1050',
    customer: {
      name: 'Ravi Chandran',
      phone: '+971 58 220 7719',
      email: 'ravi.c@mail.com',
      conversation: true,
    },
    lines: [
      {
        name: 'Pantry essentials bundle',
        variant: 'Rice, oil, lentils',
        qty: 1,
        price: 210,
        image: PANTRY,
      },
    ],
    total: 210,
    payment: 'refunded',
    status: 'cancelled',
    placed: '11 Sep 2026 · 05:40 PM',
    when: '2 days ago',
    age: 2800,
    source: 'Talab Shop',
    managedBy: 'talab',
    cancelReason: 'Unavailable',
    details: [
      {
        label: 'Delivery slot',
        value: '12 Sep, 10:00 AM – 12:00 PM',
      },
      {
        label: 'Delivery address',
        value: 'Al Karama, Dubai',
      },
    ],
    timeline: [
      {
        label: 'Order placed',
        time: '5:40 PM',
      },
      {
        label: 'Payment received',
        time: '5:41 PM',
      },
      {
        label: 'Cancelled — unavailable',
        time: '8:12 AM',
        by: 'Mariam S.',
      },
      {
        label: 'Payment refunded',
        time: '8:14 AM',
      },
    ],
  },
  {
    id: 'TLB-1048',
    customer: {
      name: 'Mohammed Ali',
      phone: '+971 50 441 2290',
      whatsapp: '+971 50 441 2290',
      email: 'm.ali@outlook.com',
      conversation: true,
    },
    lines: [
      {
        name: 'Toyota Prado 2026',
        variant: 'Monthly rental · Full insurance',
        qty: 1,
        price: 4500,
        image: PRADO,
      },
    ],
    total: 4500,
    payment: 'paid',
    status: 'new',
    placed: '13 Sep 2026 · 11:42 AM',
    when: 'Today, 11:42 AM',
    age: 34,
    source: 'Talab Shop',
    managedBy: 'talab',
    details: [
      {
        label: 'Rental period',
        value: '15 Sep – 15 Oct 2026',
      },
      {
        label: 'Delivery address',
        value: 'Business Bay, Bay Square 11, Dubai',
      },
      {
        label: 'Collection',
        value: 'Same address, 15 Oct',
      },
    ],
    instructions: 'Please deliver before 9 AM — building has morning access.',
    timeline: [
      {
        label: 'Order placed',
        time: '11:42 AM',
      },
      {
        label: 'Payment received',
        time: '11:43 AM',
      },
    ],
  },
  {
    id: 'TLB-1047',
    customer: {
      name: 'Sara Ahmed',
      phone: '+971 55 208 7741',
      whatsapp: '+971 55 208 7741',
      email: 'sara.ahmed@gmail.com',
      conversation: true,
    },
    lines: [
      {
        name: 'Airport transfer',
        variant: 'DXB Terminal 3 · Sedan',
        qty: 1,
        price: 350,
        image: CAMRY,
      },
    ],
    total: 350,
    payment: 'paid',
    status: 'progress',
    placed: '13 Sep 2026 · 10:16 AM',
    when: 'Today, 10:16 AM',
    age: 120,
    source: 'Website Chat',
    managedBy: 'talab',
    details: [
      {
        label: 'Pickup',
        value: 'DXB Terminal 3, Arrivals',
      },
      {
        label: 'Service date',
        value: '14 Sep 2026 · 6:40 AM',
      },
      {
        label: 'Drop-off',
        value: 'Jumeirah Beach Residence',
      },
    ],
    timeline: [
      {
        label: 'Order placed',
        time: '10:16 AM',
      },
      {
        label: 'Payment received',
        time: '10:16 AM',
      },
      {
        label: 'Order started',
        time: '10:31 AM',
        by: 'Rashid K.',
      },
    ],
  },
  {
    id: 'TLB-1046',
    customer: {
      name: 'Layla Hassan',
      phone: '+971 52 665 1108',
      email: 'layla.h@corpmail.ae',
    },
    lines: [
      {
        name: 'Nissan Patrol 2025',
        variant: 'Weekly rental',
        qty: 2,
        price: 1750,
        image: PATROL,
      },
      {
        name: 'Child seat',
        variant: 'Add-on',
        qty: 1,
        price: 120,
        image: CAMRY,
      },
    ],
    total: 3620,
    payment: 'pending',
    method: 'cash',
    status: 'new',
    placed: '13 Sep 2026 · 09:04 AM',
    when: 'Today, 9:04 AM',
    age: 210,
    source: 'Talab Shop',
    managedBy: 'talab',
    details: [
      {
        label: 'Rental period',
        value: '18 Sep – 25 Sep 2026',
      },
      {
        label: 'Pickup location',
        value: 'Al Quoz branch, Dubai',
      },
    ],
    instructions: 'Corporate booking — invoice to Gulf Mining LLC.',
    timeline: [
      {
        label: 'Order placed',
        time: '9:04 AM',
      },
      {
        label: 'Awaiting payment',
        time: '9:05 AM',
      },
    ],
  },
  {
    id: 'TLB-1045',
    customer: {
      name: 'Omar Sultan',
      phone: '+971 56 330 9924',
      whatsapp: '+971 56 330 9924',
      conversation: true,
    },
    lines: [
      {
        name: 'Range Rover Sport 2021',
        variant: 'Daily rental · 3 days',
        qty: 1,
        price: 2550,
        image: RANGE,
      },
    ],
    total: 2550,
    payment: 'failed',
    status: 'new',
    placed: '13 Sep 2026 · 08:20 AM',
    when: 'Today, 8:20 AM',
    age: 256,
    source: 'Talab Discover',
    managedBy: 'talab',
    details: [
      {
        label: 'Rental period',
        value: '20 Sep – 23 Sep 2026',
      },
      {
        label: 'Pickup location',
        value: 'Abu Dhabi branch',
      },
    ],
    timeline: [
      {
        label: 'Order placed',
        time: '8:20 AM',
      },
      {
        label: 'Payment failed',
        time: '8:21 AM',
      },
    ],
  },
  {
    id: 'TLB-1044',
    customer: {
      name: 'Gulf Mining LLC',
      phone: '+971 4 882 4410',
      email: 'fleet@gulfmining.ae',
    },
    lines: [
      {
        name: 'Toyota Hiace 2024',
        variant: 'Monthly lease',
        qty: 3,
        price: 3200,
        image: HIACE,
      },
    ],
    total: 9600,
    payment: 'paid',
    status: 'progress',
    placed: '12 Sep 2026 · 04:55 PM',
    when: 'Yesterday, 4:55 PM',
    age: 1150,
    source: 'Website Chat',
    managedBy: 'connected',
    externalId: 'EL-82731',
    lastSync: '2 min ago',
    details: [
      {
        label: 'Lease period',
        value: '1 Oct 2026 – 1 Apr 2027',
      },
      {
        label: 'Delivery address',
        value: 'ICAD 2, Mussafah, Abu Dhabi',
      },
      {
        label: 'Drivers',
        value: '3 assigned',
      },
    ],
    timeline: [
      {
        label: 'Order placed',
        time: '4:55 PM',
      },
      {
        label: 'Payment received',
        time: '4:56 PM',
      },
      {
        label: 'Sent to connected system',
        time: '4:56 PM',
      },
      {
        label: 'Vehicles assigned',
        time: '5:40 PM',
        by: 'Connected system',
      },
    ],
  },
  {
    id: 'TLB-1043',
    customer: {
      name: 'Fatima Noor',
      phone: '+971 50 771 0032',
      whatsapp: '+971 50 771 0032',
      email: 'fatima.noor@mail.com',
      conversation: true,
    },
    lines: [
      {
        name: 'BMW 5 Series 2023',
        variant: 'Weekly rental',
        qty: 1,
        price: 2100,
        image: BMW,
      },
    ],
    total: 2100,
    payment: 'paid',
    status: 'new',
    placed: '12 Sep 2026 · 02:10 PM',
    when: 'Yesterday, 2:10 PM',
    age: 1315,
    source: 'Talab Shop',
    managedBy: 'talab',
    details: [
      {
        label: 'Rental period',
        value: '16 Sep – 23 Sep 2026',
      },
      {
        label: 'Delivery address',
        value: 'Dubai Marina, Marina Gate 2',
      },
    ],
    timeline: [
      {
        label: 'Order placed',
        time: '2:10 PM',
      },
      {
        label: 'Payment received',
        time: '2:11 PM',
      },
    ],
  },
  {
    id: 'TLB-1042',
    customer: {
      name: 'Yousef Karim',
      phone: '+971 54 119 8820',
    },
    lines: [
      {
        name: 'Mercedes G63 2024',
        variant: 'Daily rental · 2 days',
        qty: 1,
        price: 4400,
        image: G63,
      },
    ],
    total: 4400,
    payment: 'paid',
    status: 'progress',
    placed: '12 Sep 2026 · 11:30 AM',
    when: 'Yesterday, 11:30 AM',
    age: 1475,
    source: 'Talab Discover',
    managedBy: 'connected',
    externalId: 'EL-82698',
    lastSync: '11 min ago',
    details: [
      {
        label: 'Rental period',
        value: '14 Sep – 16 Sep 2026',
      },
      {
        label: 'Pickup location',
        value: 'Al Quoz branch, Dubai',
      },
    ],
    timeline: [
      {
        label: 'Order placed',
        time: '11:30 AM',
      },
      {
        label: 'Payment received',
        time: '11:31 AM',
      },
      {
        label: 'Vehicle assigned',
        time: '12:04 PM',
        by: 'Connected system',
      },
    ],
  },
  {
    id: 'TLB-1041',
    customer: {
      name: 'Aisha Rahman',
      phone: '+971 55 640 2217',
      whatsapp: '+971 55 640 2217',
      conversation: true,
    },
    lines: [
      {
        name: 'Toyota Camry 2024',
        variant: 'Monthly rental',
        qty: 1,
        price: 2400,
        image: CAMRY,
      },
    ],
    total: 2400,
    payment: 'paid',
    status: 'fulfilled',
    placed: '10 Sep 2026 · 09:15 AM',
    when: '3 days ago',
    age: 4400,
    source: 'Talab Shop',
    managedBy: 'talab',
    fulfilledBy: 'Rashid K.',
    fulfilledAt: '10 Sep 2026 · 2:18 PM',
    details: [
      {
        label: 'Rental period',
        value: '10 Sep – 10 Oct 2026',
      },
      {
        label: 'Delivery address',
        value: 'Al Barsha 1, Dubai',
      },
    ],
    timeline: [
      {
        label: 'Order placed',
        time: '9:15 AM',
      },
      {
        label: 'Payment received',
        time: '9:16 AM',
      },
      {
        label: 'Order started',
        time: '9:48 AM',
        by: 'Rashid K.',
      },
      {
        label: 'Fulfilled',
        time: '2:18 PM',
        by: 'Rashid K.',
      },
    ],
  },
  {
    id: 'TLB-1040',
    customer: {
      name: 'Khalid Bin Saeed',
      phone: '+971 50 220 4471',
      email: 'khalid@bsholdings.ae',
    },
    lines: [
      {
        name: 'Nissan Patrol 2025',
        variant: 'Monthly rental',
        qty: 1,
        price: 5200,
        image: PATROL,
      },
      {
        name: 'Airport pickup',
        variant: 'Add-on',
        qty: 1,
        price: 200,
        image: CAMRY,
      },
    ],
    total: 5400,
    payment: 'paid',
    status: 'fulfilled',
    placed: '8 Sep 2026 · 03:40 PM',
    when: '5 days ago',
    age: 7300,
    source: 'Website Chat',
    managedBy: 'talab',
    fulfilledBy: 'Noura A.',
    fulfilledAt: '9 Sep 2026 · 10:05 AM',
    details: [
      {
        label: 'Rental period',
        value: '9 Sep – 9 Oct 2026',
      },
      {
        label: 'Pickup',
        value: 'DXB Terminal 1, Arrivals',
      },
    ],
    timeline: [
      {
        label: 'Order placed',
        time: '3:40 PM',
      },
      {
        label: 'Payment received',
        time: '3:41 PM',
      },
      {
        label: 'Order started',
        time: '4:02 PM',
        by: 'Noura A.',
      },
      {
        label: 'Fulfilled',
        time: '10:05 AM',
        by: 'Noura A.',
      },
    ],
  },
  {
    id: 'TLB-1039',
    customer: {
      name: 'Reem Al Suwaidi',
      phone: '+971 56 909 3312',
      whatsapp: '+971 56 909 3312',
    },
    lines: [
      {
        name: 'Toyota Prado 2026',
        variant: 'Weekly rental',
        qty: 1,
        price: 2800,
        image: PRADO,
      },
    ],
    total: 2800,
    payment: 'paid',
    status: 'fulfilled',
    placed: '6 Sep 2026 · 12:20 PM',
    when: '7 days ago',
    age: 10200,
    source: 'Talab Shop',
    managedBy: 'talab',
    fulfilledBy: 'Rashid K.',
    fulfilledAt: '6 Sep 2026 · 5:02 PM',
    details: [
      {
        label: 'Rental period',
        value: '6 Sep – 13 Sep 2026',
      },
      {
        label: 'Delivery address',
        value: 'Khalifa City A, Abu Dhabi',
      },
    ],
    timeline: [
      {
        label: 'Order placed',
        time: '12:20 PM',
      },
      {
        label: 'Payment received',
        time: '12:21 PM',
      },
      {
        label: 'Order started',
        time: '12:55 PM',
        by: 'Rashid K.',
      },
      {
        label: 'Fulfilled',
        time: '5:02 PM',
        by: 'Rashid K.',
      },
    ],
  },
  {
    id: 'TLB-1038',
    customer: {
      name: 'Daniel Meyer',
      phone: '+971 58 447 1190',
      email: 'd.meyer@expatmail.com',
      conversation: true,
    },
    lines: [
      {
        name: 'BMW 5 Series 2023',
        variant: 'Daily rental · 4 days',
        qty: 1,
        price: 1600,
        image: BMW,
      },
    ],
    total: 1600,
    payment: 'refunded',
    status: 'cancelled',
    placed: '5 Sep 2026 · 06:12 PM',
    when: '8 days ago',
    age: 11800,
    source: 'Talab Discover',
    managedBy: 'talab',
    cancelReason: 'Customer requested',
    details: [
      {
        label: 'Rental period',
        value: '7 Sep – 11 Sep 2026',
      },
    ],
    timeline: [
      {
        label: 'Order placed',
        time: '6:12 PM',
      },
      {
        label: 'Payment received',
        time: '6:13 PM',
      },
      {
        label: 'Cancelled — customer requested',
        time: '9:40 AM',
        by: 'Noura A.',
      },
      {
        label: 'Payment refunded',
        time: '9:42 AM',
      },
    ],
  },
  {
    id: 'TLB-1037',
    customer: {
      name: 'Hind Al Marri',
      phone: '+971 52 118 7740',
    },
    lines: [
      {
        name: 'Toyota Hiace 2024',
        variant: 'Weekly rental',
        qty: 1,
        price: 1900,
        image: HIACE,
      },
    ],
    total: 1900,
    payment: 'failed',
    status: 'cancelled',
    placed: '3 Sep 2026 · 10:02 AM',
    when: '10 days ago',
    age: 14600,
    source: 'Talab Shop',
    managedBy: 'talab',
    cancelReason: 'Payment issue',
    details: [
      {
        label: 'Rental period',
        value: '4 Sep – 11 Sep 2026',
      },
    ],
    timeline: [
      {
        label: 'Order placed',
        time: '10:02 AM',
      },
      {
        label: 'Payment failed',
        time: '10:03 AM',
      },
      {
        label: 'Cancelled — payment issue',
        time: '4:15 PM',
        by: 'Rashid K.',
      },
    ],
  },
  /* Completed, but the cash was never recorded — the case that needs a nudge. */
  {
    id: '#TLB-1026',
    customer: {
      name: 'Ahmed Hassan',
      phone: '+971 50 441 9022',
      whatsapp: '+971 50 441 9022',
      email: 'ahmed.hassan@gmail.com',
      conversation: true,
    },
    lines: [
      {
        name: 'Toyota Camry 2025',
        variant: 'Weekly rental',
        qty: 1,
        price: 1250,
        image: CAMRY,
      },
    ],
    total: 1250,
    payment: 'pending',
    method: 'cash',
    status: 'fulfilled',
    placed: '8 Sep 2026 · 08:40 AM',
    when: '5 days ago',
    age: 7400,
    source: 'Talab Shop',
    managedBy: 'talab',
    details: [
      {
        label: 'Rental period',
        value: '8 Sep – 15 Sep 2026',
      },
      {
        label: 'Collection',
        value: 'Al Quoz branch',
      },
    ],
    fulfilledBy: 'Rashid K.',
    fulfilledAt: '8 Sep 2026 · 9:25 AM',
    timeline: [
      {
        label: 'Order placed',
        time: '08:40 AM',
      },
      {
        label: 'Order started',
        time: '08:52 AM',
        by: 'Rashid K.',
      },
      {
        label: 'Fulfilled',
        time: '09:25 AM',
        by: 'Rashid K.',
      },
    ],
  },
]
